/**
 * ProductDataPanel - the equipment registry for one project + discipline +
 * zone. Collapsible card above the checklist tree.
 *
 * Every piece of hardware needs different things recorded, so there is NO
 * fixed schema: an entry is created from just its name ("Dell PC" -> Add),
 * then gets any number of custom label/value fields - Part Number, Serial
 * Number, Service Tag, IP Address... whatever that device calls for. Common
 * labels are offered via a native datalist but anything can be typed.
 *
 * Self-contained: fetches per zone; add/edit/delete inline; a freshly added
 * entry opens straight into field editing so the flow is one motion.
 */
import { useCallback, useEffect, useState } from 'react';
import { SVM_PRODUCT_DATA_URL, getJson, sendJson, svmProductDataUrl } from './svmApi';
import { BTN_GHOST, BTN_PRIMARY, CARD, GREEN, INPUT } from './svmModel';

const LINK = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
  fontSize: 10.5,
  fontWeight: 700,
};
const LABEL_SUGGESTIONS = [
  'Part Number',
  'Serial Number',
  'Service Tag',
  'Model',
  'Asset Tag',
  'IP Address',
  'MAC Address',
  'Firmware Version',
  'Software Version',
  'Manufacture Date',
];

/** One equipment entry's editor: name + a dynamic list of label/value rows. */
function EntryEditor({ entry, narrow = false, busy = false, onSave, onCancel }) {
  const [name, setName] = useState(entry.equipment);
  // Always keep one trailing empty row ready to type into.
  const [fields, setFields] = useState(() => [...(entry.fields || []), { label: '', value: '' }]);
  const setField = (i, k, v) => setFields((prev) => prev.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const removeField = (i) => setFields((prev) => prev.filter((_, j) => j !== i));
  const addField = () => setFields((prev) => [...prev, { label: '', value: '' }]);
  const fld = { ...INPUT, fontSize: 11.5, padding: '7px 9px' };
  const cleaned = fields.map((f) => ({ label: f.label.trim(), value: f.value.trim() })).filter((f) => f.label && f.value);
  const halfDone = fields.some((f) => (f.label.trim() ? !f.value.trim() : !!f.value.trim()));
  return (
    <div style={{ background: '#F6F9F3', border: '1px solid #DCE7D6', borderRadius: 8, padding: '10px 12px' }}>
      <input
        style={{ ...fld, fontWeight: 700, marginBottom: 8 }}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Equipment name (e.g. Dell PC, Robot R1)"
      />
      {fields.map((f, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <input
            list="pd-field-labels"
            style={{ ...fld, flex: '1 1 110px' }}
            value={f.label}
            onChange={(e) => setField(i, 'label', e.target.value)}
            placeholder="Field (e.g. Part Number)"
          />
          <input
            style={{ ...fld, flex: '1 1 140px', fontFamily: 'Consolas, monospace' }}
            value={f.value}
            onChange={(e) => setField(i, 'value', e.target.value)}
            placeholder="Value"
          />
          <button
            type="button"
            aria-label="Remove field"
            onClick={() => removeField(i)}
            style={{ ...LINK, color: '#c08a8a', fontSize: 14, padding: '0 3px', flexShrink: 0 }}
          >
            ×
          </button>
        </div>
      ))}
      <datalist id="pd-field-labels">
        {LABEL_SUGGESTIONS.map((l) => (
          <option key={l} value={l} />
        ))}
      </datalist>
      <button type="button" onClick={addField} style={{ ...LINK, color: GREEN, fontSize: 11 }}>
        + Add another field
      </button>
      {halfDone && <div style={{ marginTop: 6, fontSize: 10, color: '#B26A00' }}>Rows missing a field name or a value are not saved.</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={onCancel} style={{ ...BTN_GHOST, padding: '7px 14px', fontSize: 11.5, color: '#B71C1C' }}>
          Cancel
        </button>
        <button
          type="button"
          disabled={!name.trim() || busy}
          onClick={() => onSave(name.trim(), cleaned)}
          style={{ ...(!name.trim() || busy ? BTN_GHOST : BTN_PRIMARY), padding: '7px 16px', fontSize: 11.5 }}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function ProductDataPanel({ projectLabel, disciplineId, zone, narrow = false }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null); // entry id whose editor is open
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getJson(svmProductDataUrl(projectLabel, disciplineId, zone));
      setRows(d.rows || []);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load product data');
    } finally {
      setLoaded(true);
    }
  }, [projectLabel, disciplineId, zone]);
  useEffect(() => {
    setLoaded(false);
    setRows([]);
    setEditingId(null);
    load();
  }, [load]);

  // "Dell PC" -> Add: creates the entry and drops straight into field editing.
  const add = useCallback(async () => {
    const equipment = newName.trim();
    if (!equipment || busy) return;
    setBusy(true);
    try {
      const d = await sendJson('POST', SVM_PRODUCT_DATA_URL, { project: projectLabel, discipline: disciplineId, zone, equipment });
      setRows((prev) => [...prev, d.row]);
      setNewName('');
      setEditingId(d.row.id);
      setError('');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [newName, busy, projectLabel, disciplineId, zone]);

  const saveEntry = useCallback(async (id, equipment, fields) => {
    setBusy(true);
    try {
      const d = await sendJson('PUT', `${SVM_PRODUCT_DATA_URL}/${id}`, { equipment, fields });
      setRows((prev) => prev.map((r) => (r.id === id ? d.row : r)));
      setEditingId(null);
      setError('');
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, []);

  const remove = useCallback(
    async (id) => {
      try {
        await sendJson('DELETE', `${SVM_PRODUCT_DATA_URL}/${id}`, {});
        setRows((prev) => prev.filter((r) => r.id !== id));
        if (editingId === id) setEditingId(null);
      } catch (e) {
        setError(e.message || 'Delete failed');
      }
    },
    [editingId]
  );

  const totalFields = rows.reduce((s, r) => s + (r.fields || []).length, 0);

  return (
    <div style={{ ...CARD, padding: narrow ? '10px 12px' : '12px 16px', marginBottom: 14 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontFamily: "'Montserrat', sans-serif",
        }}
      >
        <span style={{ fontSize: 11.5, fontWeight: 800, color: GREEN, letterSpacing: 1, textTransform: 'uppercase' }}>
          Product Data · {zone}
        </span>
        <span style={{ fontSize: 10, color: '#999', fontWeight: 600 }}>
          {loaded ? `${rows.length} equipment · ${totalFields} field${totalFields === 1 ? '' : 's'}` : '…'}
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 12,
            color: '#999',
            transform: open ? 'rotate(90deg)' : 'none',
            display: 'inline-block',
            transition: 'transform 0.15s',
          }}
        >
          ▸
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {error && (
            <div
              style={{
                fontSize: 11,
                color: '#B71C1C',
                background: '#FFEBEE',
                border: '1px solid #FFCDD2',
                borderRadius: 8,
                padding: '5px 10px',
                marginBottom: 8,
              }}
            >
              {error}
            </div>
          )}

          {/* equipment cards */}
          {rows.map((r) => (
            <div key={r.id} style={{ marginBottom: 8 }}>
              {editingId === r.id ? (
                <EntryEditor
                  entry={r}
                  narrow={narrow}
                  busy={busy}
                  onSave={(name, fields) => saveEntry(r.id, name, fields)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div style={{ background: '#fff', border: '1px solid #E6EBE1', borderRadius: 8, padding: '9px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: '#1a1a1a' }}>{r.equipment}</span>
                    <span style={{ fontSize: 9, color: '#aaa' }}>{r.recordedBy || ''}</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" style={{ ...LINK, color: '#E65100' }} onClick={() => setEditingId(r.id)}>
                      {(r.fields || []).length ? 'Edit fields' : '+ Add fields'}
                    </button>
                    <button type="button" style={{ ...LINK, color: '#B71C1C' }} onClick={() => remove(r.id)}>
                      Delete
                    </button>
                  </div>
                  {(r.fields || []).length > 0 && (
                    <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: '3px 18px' }}>
                      {r.fields.map((f, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={i} style={{ fontSize: 11, display: 'flex', gap: 6, minWidth: 0 }}>
                          <span style={{ color: '#8a8a8a', fontWeight: 700, flexShrink: 0 }}>{f.label}:</span>
                          <span
                            style={{
                              color: '#222',
                              fontFamily: 'Consolas, monospace',
                              fontSize: 11,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {f.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {loaded && rows.length === 0 && (
            <div style={{ fontSize: 11, color: '#bbb', padding: '2px 0 8px' }}>
              No equipment recorded for {zone} yet - add its name below, then its fields.
            </div>
          )}

          {/* add equipment: name only - fields come right after */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <input
              style={{ ...INPUT, fontSize: 11.5, padding: '7px 9px', flex: 1 }}
              placeholder="Equipment name (e.g. Dell PC, Robot R1, Main PLC)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
            <button
              type="button"
              onClick={add}
              disabled={!newName.trim() || busy}
              style={{ ...(!newName.trim() || busy ? BTN_GHOST : BTN_PRIMARY), padding: '8px 16px', fontSize: 11.5 }}
            >
              {busy ? 'Saving…' : '+ Add'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
